import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "../App.css";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_BASE}?action=config`;

const DEFAULT_GROUP_ORDER = [
  "TS API", "TrackShip Account", "TSAI", "TrackShip front",
  "TrackShip Docs", "Zorem", "Zorem Docs", "Todo",
  "Shopify", "Company TS", "Apps", "TrackShip", "Inearu",
];

function loadSaved() {
  try {
    const raw = localStorage.getItem("selectData");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const obj = {};
      parsed.forEach((item) => { if (item.id) obj[item.id] = !!item.select; });
      return obj;
    }
    return parsed;
  } catch {
    return {};
  }
}

export default function Dashboard() {
  const [sites, setSites] = useState([]);
  const [groupOrder, setGroupOrder] = useState(DEFAULT_GROUP_ORDER);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API_URL);
        const saved = loadSaved();

        let rawSites, rawOrder;
        if (Array.isArray(res.data)) {
          rawSites = res.data;
          rawOrder = DEFAULT_GROUP_ORDER;
        } else {
          rawSites = res.data.sites || [];
          rawOrder = res.data.group_order || DEFAULT_GROUP_ORDER;
        }

        const items = rawSites.map((item) => ({
          ...item,
          label: item.label.replace(/\n/g, " ").trim(),
        }));

        const initialSelected = {};
        items.forEach((item) => {
          initialSelected[item.id] =
            saved[item.id] !== undefined ? saved[item.id] : !!item.select;
        });

        setSites(items);
        setGroupOrder(rawOrder);
        setSelected(initialSelected);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const persist = (next) => {
    localStorage.setItem("selectData", JSON.stringify(next));
    setSelected(next);
  };

  const toggleSite = (id) => persist({ ...selected, [id]: !selected[id] });
  const toggleGroup = (ids, value) => {
    const next = { ...selected };
    ids.forEach((id) => (next[id] = value));
    persist(next);
  };
  const selectAll = (value) => {
    const next = {};
    sites.forEach((s) => (next[s.id] = value));
    persist(next);
  };

  const groups = useMemo(() => {
    const map = {};
    sites.forEach((item) => {
      if (!map[item.group]) map[item.group] = [];
      map[item.group].push(item);
    });
    const sorted = {};
    groupOrder.forEach((g) => { if (map[g]) sorted[g] = map[g]; });
    Object.keys(map).forEach((g) => { if (!sorted[g]) sorted[g] = map[g]; });
    return sorted;
  }, [sites, groupOrder]);

  const anySelected = Object.values(selected).some(Boolean);

  return (
    <div className="app">
      <header className="filter-bar">
        <div className="filter-bar-inner">
          <div className="filter-actions">
            <button className="text-btn" onClick={() => selectAll(true)}>All</button>
            <span className="divider">|</span>
            <button className="text-btn" onClick={() => selectAll(false)}>None</button>
          </div>
          <div className="group-pills">
            {Object.entries(groups).map(([group, items]) => (
              <GroupPill
                key={group}
                name={group}
                items={items}
                selected={selected}
                onToggleGroup={toggleGroup}
                onToggleSite={toggleSite}
              />
            ))}
          </div>
          <div className="user-bar">
            <a href="#admin" className="text-btn">Admin</a>
          </div>
        </div>
      </header>

      <main className="main-content">
        {Object.entries(groups).map(([group, items]) => {
          const visible = items.filter((i) => selected[i.id]);
          if (!visible.length) return null;
          return (
            <section key={group} className="project-section">
              <h2 className="project-title">
                {group}
                <span className="project-count">{visible.length}</span>
              </h2>
              <div className="cards-row">
                {visible.map((item) => (
                  <SiteCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          );
        })}
        {!anySelected && (
          <div className="empty-state">
            <p>No sites selected.</p>
            <p>Use the filter bar above to choose which sites to display.</p>
          </div>
        )}
      </main>
    </div>
  );
}

function GroupPill({ name, items, selected, onToggleGroup, onToggleSite }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const ids = items.map((i) => i.id);
  const onCount = ids.filter((id) => selected[id]).length;
  const allOn = onCount === ids.length;
  const someOn = onCount > 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className={`group-pill ${allOn ? "all-on" : someOn ? "some-on" : ""}`}>
      <input
        type="checkbox"
        checked={allOn}
        ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
        onChange={(e) => onToggleGroup(ids, e.target.checked)}
      />
      <span className="pill-name" onClick={() => setOpen((o) => !o)}>{name}</span>
      <span className="pill-dots">
        {items.map((item) => (
          <button
            key={item.id}
            className={`env-dot env-${item.env} ${selected[item.id] ? "on" : ""}`}
            title={item.label}
            onClick={() => onToggleSite(item.id)}
          />
        ))}
      </span>
      {open && (
        <div className="pill-dropdown">
          {items.map((item) => (
            <label key={item.id} className="dropdown-item">
              <input
                type="checkbox"
                checked={!!selected[item.id]}
                onChange={() => onToggleSite(item.id)}
              />
              <span className={`env-dot env-${item.env} on`} />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function HostEntry({ ip, domain }) {
  const [copied, setCopied] = useState(false);
  if (!ip) return null;

  const entry = `${ip}  ${domain}`;

  const copy = () => {
    navigator.clipboard.writeText(entry).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="host-entry">
      <svg className="host-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
      </svg>
      <code className="host-code">{entry}</code>
      <button className="host-copy-btn" onClick={copy} title="Copy hosts entry">
        {copied ? "✓" : "Copy"}
      </button>
    </div>
  );
}

function SiteCard({ item }) {
  const [status, setStatus] = useState("probing");
  const [iframeKey, setIframeKey] = useState(0);

  const hostname = (() => {
    try { return new URL(item.iframe).hostname; } catch { return ""; }
  })();

  const probe = useCallback(() => {
    setStatus("probing");
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    fetch(item.iframe, { mode: "no-cors", signal: controller.signal })
      .then(() => {
        clearTimeout(timer);
        setStatus("ok");
        setIframeKey((k) => k + 1);
      })
      .catch(() => {
        clearTimeout(timer);
        setStatus("error");
      });
  }, [item.iframe]);

  useEffect(() => { probe(); }, [probe]);

  return (
    <div className="site-card">
      <div className="site-card-header">
        <span className="site-label">{item.label}</span>
        <span className={`env-badge env-badge-${item.env}`}>{item.env.toUpperCase()}</span>
        <button className="iframe-reload-btn" onClick={probe} title="Reload">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
          </svg>
        </button>
        <a href={item.iframe} target="_blank" rel="noreferrer" className="site-link" title="Open site">↗</a>
      </div>

      {item.host_ip && <HostEntry ip={item.host_ip} domain={hostname} />}

      <div className="iframe-wrapper">
        {status === "probing" && (
          <div className="ssl-overlay">
            <div className="probe-spinner" />
            <p className="ssl-desc">Checking…</p>
          </div>
        )}
        {status === "error" && (
          <div className="ssl-overlay">
            <svg className="ssl-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <p className="ssl-title">Site unreachable</p>
            <p className="ssl-desc">
              SSL certificate may not be trusted, or the host isn't reachable.<br />
              Open in a new tab, accept the warning, then retry.
            </p>
            <a href={item.iframe} target="_blank" rel="noreferrer" className="ssl-open-btn">Open in new tab ↗</a>
            <button className="ssl-trusted-btn" onClick={probe}>Retry</button>
          </div>
        )}
        {status === "ok" && (
          <iframe key={iframeKey} className="site-iframe" src={item.iframe} title={item.label} />
        )}
      </div>
    </div>
  );
}
